import { useEffect } from 'react';
// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { FormHelperText, Box, useTheme } from '@mui/material';
//
import Editor, { EditorProps } from '../editor';

// ----------------------------------------------------------------------

interface Props extends EditorProps {
  name: string;
  disabled?: boolean;
  simple?: boolean;
}

export default function RHFEditor({ name, helperText, disabled, ...other }: Props) {
  const {
    control,
    watch,
    setValue,
    formState: { isSubmitSuccessful },
  } = useFormContext();

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const values = watch();

  useEffect(() => {
    if (values[name] === '<p><br></p>') {
      setValue(name, '', {
        shouldValidate: !isSubmitSuccessful,
      });
    }
  }, [isSubmitSuccessful, name, setValue, values]);

  useEffect(() => {
    // Добавляем задержку для корректной инициализации Quill после переключения режимов
    const timer = setTimeout(() => {
      const editor = document.querySelector(`#${name} .ql-editor`);
      if (editor && values[name] === '<p><br></p>') {
        setValue(name, '', {
          shouldValidate: !isSubmitSuccessful,
        });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [values[name], name, setValue, isSubmitSuccessful]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box
          sx={{
            '& .ql-editor': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[800] : '#fff'} !important`,
              color: `${isDarkMode ? theme.palette.common.white : theme.palette.text.primary} !important`,
              '&.ql-blank::before': {
                color: `${isDarkMode ? theme.palette.grey[400] : theme.palette.text.disabled} !important`,
              },
            },
            '& .ql-toolbar': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[900] : '#fff'} !important`,
              borderColor: `${isDarkMode ? theme.palette.grey[700] : theme.palette.grey[300]} !important`,
            },
            '& .ql-container': {
              borderColor: `${isDarkMode ? theme.palette.grey[700] : theme.palette.grey[300]} !important`,
              backgroundColor: `${isDarkMode ? theme.palette.grey[800] : '#fff'} !important`,
            },
            '& .ql-toolbar .ql-stroke': {
              stroke: `${isDarkMode ? theme.palette.grey[300] : theme.palette.text.primary} !important`,
            },
            '& .ql-toolbar .ql-fill': {
              fill: `${isDarkMode ? theme.palette.grey[300] : theme.palette.text.primary} !important`,
            },
            '& .ql-toolbar button:hover': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[700] : theme.palette.grey[100]} !important`,
            },
            '& .ql-toolbar button.ql-active': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[600] : theme.palette.grey[200]} !important`,
            },
            // Дополнительные селекторы для форсирования стилей
            '& .ql-snow .ql-editor': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[800] : '#fff'} !important`,
              color: `${isDarkMode ? theme.palette.common.white : theme.palette.text.primary} !important`,
            },
            '& .ql-snow .ql-toolbar': {
              backgroundColor: `${isDarkMode ? theme.palette.grey[900] : '#fff'} !important`,
            },
            // Стили для параграфов в редакторе
            '& .ql-editor p': {
              marginBlockStart: '1em',
              marginBlockEnd: '1em',
              marginInlineStart: '0px',
              marginInlineEnd: '0px',
            },
          }}
        >
          <Editor
            id={name}
            value={field.value}
            onChange={disabled ? () => {} : field.onChange}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            {...other}
          />
        </Box>
      )}
    />
  );
}
